/**
 * App version management utilities.
 * Handles version checking and refresh logic for automatic updates.
 * Can be used by kiosk, companion app, or any other frontend directory.
 */

import { datadogLogs } from "@datadog/browser-logs";

const VERSION_CHECK_ENDPOINT = "/api/version";

export interface VersionInfo {
  version: string;
  releasedAt: string;
  buildId: string;
}

/**
 * Get the version baked into the current build.
 */
export function getBuildVersion(): string {
  return process.env.NEXT_PUBLIC_APP_VERSION || "0.0.0";
}

/**
 * Get the build ID baked into the current build.
 * Now the same as the version (commit SHA).
 */
export function getBuildId(): string {
  return process.env.NEXT_PUBLIC_APP_VERSION || "local";
}

/**
 * Get the release timestamp baked into the current build.
 */
export function getBuildReleasedAt(): string | null {
  return process.env.NEXT_PUBLIC_APP_RELEASED_AT || null;
}

/**
 * Get the version stored in localStorage.
 */
export function getStoredVersion(storageKey: string): string | null {
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

/**
 * Store the current version in localStorage.
 */
export function storeVersion(storageKey: string, version: string): void {
  try {
    localStorage.setItem(storageKey, version);
  } catch (error) {
    datadogLogs.logger.warn("[app] Failed to store version", { error });
  }
}

/**
 * Fetch the current server version from the API.
 * Uses no-store to bypass cache and get fresh data.
 */
export async function fetchServerVersion(): Promise<VersionInfo | null> {
  try {
    const response = await fetch(VERSION_CHECK_ENDPOINT, {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache",
      },
    });
    if (!response.ok) return null;

    const result = await response.json();
    return result;
  } catch (error) {
    datadogLogs.logger.warn("[app] Failed to fetch server version", {
      error,
    });
    return null;
  }
}

/**
 * Check if the app should refresh to get a new version.
 * Compares build IDs (commit SHAs) to detect any deployment change.
 * @param logPrefix - Optional prefix for logs (e.g., "[kiosk]", "[companion]")
 */
export async function shouldRefreshForUpdate(
  logPrefix = "[app]",
): Promise<boolean> {
  const localBuildId = getBuildId();
  const localVersion = getBuildVersion();
  const serverInfo = await fetchServerVersion();

  if (!serverInfo) {
    datadogLogs.logger.warn(
      `${logPrefix} Could not fetch server version, skipping update check`,
    );
    return false;
  }

  const needsUpdate = serverInfo.buildId !== localBuildId;

  if (needsUpdate) {
    datadogLogs.logger.info(`${logPrefix} Update available`, {
      currentVersion: localVersion,
      serverVersion: serverInfo.version,
      currentBuildId: localBuildId,
      serverBuildId: serverInfo.buildId,
    });
  }

  return needsUpdate;
}

/**
 * Perform a full page refresh to load the new version.
 * @param logPrefix - Optional prefix for logs
 * @param storageKey - localStorage key for storing version
 */
export function refreshForUpdate(
  logPrefix = "[app]",
  storageKey = "app_version",
): void {
  datadogLogs.logger.info(`${logPrefix} Refreshing for update`, {
    currentVersion: getBuildVersion(),
  });
  storeVersion(storageKey, getBuildVersion());
  window.location.reload();
}

/**
 * Initialize version tracking on app start.
 * @param logPrefix - Optional prefix for logs
 * @param storageKey - localStorage key for storing version
 */
export function initializeVersionTracking(
  logPrefix = "[app]",
  storageKey = "app_version",
): void {
  const buildVersion = getBuildVersion();
  const storedVersion = getStoredVersion(storageKey);

  if (storedVersion !== buildVersion) {
    datadogLogs.logger.info(`${logPrefix} Version changed on mount`, {
      previousVersion: storedVersion || "none",
      currentVersion: buildVersion,
    });
    storeVersion(storageKey, buildVersion);
  }
}
